/**
 * DataGridPanel is a "private" commponent that is used by SingleSelectPanel and MultiSelectPanel.
 * This component is NOT exposed as part of the component library.
 */
import classNames from 'classnames';
import { FormikProps, FormikValues } from 'formik';
import React, { Component, PropsWithChildren, Fragment, ReactElement, RefObject } from 'react';
import { Button, DropdownButton, Form, MenuItem, Modal, ModalBody, ModalFooter, ModalHeader } from '..';
import { IFormRenderProps } from '../Form';
import { IDataGridCommonRenderProps as IDisplayPanelProps } from '../Grid/DataGrid';
import { fontAwesome, Icon } from '../Icon';
import './DataGridPanel.scss';
import { IDataGridPanelProps, IFormikProps, IMultiProps, ISingleProps } from './types';

import { faTimes } from '@fortawesome/pro-light-svg-icons';
fontAwesome.library.add(faTimes as any);

const DGP = 'hlc-dgp-'; // prefix for classNames  dgp = data grid panel

export class DataGridPanel<T> extends Component<PropsWithChildren<IDataGridPanelProps<T>>, { confirm: boolean }> {
  public static multi<T>(props: IMultiProps<T>) {
    return DataGridPanel.getPanel<T>('multi', props);
  }

  public static single<T>(props: ISingleProps<T>) {
    return DataGridPanel.getPanel<T>('single', props);
  }

  private static getPanel<T>(type: 'multi' | 'single', props: ISingleProps<T> | IMultiProps<T>) {
    const { displayPanelProps, ...other } = props;
    const { rowData } = displayPanelProps;
    return (
      <DataGridPanel<T>
        type={type}
        displayPanelProps={displayPanelProps}
        row={rowData.single}
        rows={rowData.multi}
        {...other}
      />
    );
  }
  public state = {
    confirm: false,
  };
  private formikRenderProps: FormikProps<FormikValues> & IFormRenderProps;
  private panelContainer: RefObject<HTMLDivElement> = React.createRef();

  public render() {
    const { children: form } = this.props;
    const hasForm = !!form;

    return (
      <Fragment>
        {hasForm && this.renderPanelInForm()}
        {!hasForm && this.renderPanel()}
      </Fragment>
    );
  }

  private handleButtonClick = (e: React.SyntheticEvent<any> | null, key: string) => {
    const { displayPanelProps, onButtonClick = (k: string, d: IDisplayPanelProps<T>) => k } = this.props;

    onButtonClick(key, displayPanelProps);

    if (e !== null) {
      e.preventDefault();
    }
  };

  private handleClose = () => {
    const {
      children: form,
      displayPanelProps: { handleDisplayClose },
    } = this.props;
    const { dirty, resetForm } = this.formikRenderProps || {
      dirty: undefined,
      resetForm: undefined,
    };

    if (dirty) {
      this.setState({ confirm: true });
    } else {
      handleDisplayClose();
    }
  };

  private maybe(section: 'preview' | 'kpi') {
    const { displayPanelProps } = this.props;
    const fn = this.props[section];
    return fn ? <div className={`${DGP}${section}`}>{fn(displayPanelProps)}</div> : null;
  }

  private maybeActions() {
    const { actions } = this.props;

    if (actions) {
      // need to make sure that there is at most ONE dropdown button
      let buttonActions = actions.filter((a) => typeof a === 'string');
      const dropdown = actions.find((a) => typeof a === 'object');
      if (dropdown) {
        buttonActions = [...buttonActions, dropdown];
      }

      return (
        <div className={`${DGP}actions`}>
          {buttonActions.map((key: string | string[]) => {
            return Array.isArray(key) ? this.renderDropdownButton(key as string[]) : this.renderButton(key as string);
          })}
        </div>
      );
    } else {
      return null;
    }
  }

  private renderButton(key: string) {
    return <Button label={key} key={key} color="default" onClick={(e) => this.handleButtonClick(e, key)} />;
  }

  private renderDropdownButton(options: string[]) {
    return (
      <DropdownButton
        key={'more'}
        title="More"
        onSelect={(key) => {
          this.handleButtonClick(null, key);
        }}
      >
        {options.map((option) => {
          return (
            <MenuItem key={option} eventKey={option}>
              {option}
            </MenuItem>
          );
        })}
      </DropdownButton>
    );
  }

  private maybeButtons(props: FormikProps<FormikValues>) {
    const { dirty, resetForm } = props;

    if (!dirty) {
      return null;
    } else {
      return (
        <div className={`${DGP}footer`}>
          <Button type={'submit'} label={'Save'} color={'primary'} />
          <Button
            label={'Cancel'}
            color={'default'}
            onClick={(e) => {
              e.preventDefault();
              resetForm();
            }}
          />
        </div>
      );
    }
  }

  private maybeHamburger() {
    const { hamburger } = this.props;
    if (hamburger) {
      return (
        <DropdownButton ellipsis={true} align={'right'}>
          {hamburger.map((key) => (
            <MenuItem eventKey={key} key={key} onSelect={(k) => this.handleButtonClick(null, key)}>
              {key}
            </MenuItem>
          ))}
        </DropdownButton>
      );
    } else {
      return null;
    }
  }

  private maybeIcon() {
    const { icon } = this.props;
    if (icon === undefined) {
      return null;
    } else if (typeof icon === 'function') {
      return <div className={`${DGP}icon`}>{icon()}</div>;
    } else {
      return (
        <div className={`${DGP}icon`}>
          <Icon icon={icon} size={'5x'} style={{ color: '#5CA2EC' }} />
        </div>
      );
    }
  }

  private multiselectTitle() {
    const {
      displayPanelProps: { deselectAll, toggleViewSelected, viewSelected },
      rows,
      title = 'Rows Selected',
    } = this.props;

    return (
      <div className={`${DGP}title`}>
        <div className={`${DGP}title-count`}>{rows.length}</div>
        <div className={`${DGP}title-text`}>{title}</div>
        <div className={`${DGP}title-actions`}>
          <Button label="Clear selections" color="link" onClick={(e) => deselectAll()} type="button" />
          <span />
          <Button
            label={viewSelected ? 'View all' : 'View selected'}
            color="link"
            onClick={toggleViewSelected}
            type="button"
          />
        </div>
      </div>
    );
  }

  private renderPanelInForm() {
    // render our panel inside a Form
    const { children: form } = this.props;
    // we've already previously checked that form exists, here
    const { render, ...formProps } = React.Children.only(form)!.props;
    return (
      <Fragment>
        <Form
          enableReinitialize={true}
          render={(props) => {
            this.formikRenderProps = props;
            return this.renderPanel({ render, props });
          }}
          {...formProps}
        />
        <Modal show={this.state.confirm} size={'small'} onHide={() => this.setState({ confirm: false })}>
          <ModalHeader title={'Discard Changes?'} />
          <ModalBody>
            <p>You haven't saved your changes. Would you like to discard your changes?</p>
          </ModalBody>
          <ModalFooter>
            <Button label={'Continue Editing'} onClick={() => this.setState({ confirm: false })} />
            <Button
              label={'Discard Changes'}
              color={'primary'}
              onClick={this.props.displayPanelProps.handleDisplayClose}
            />
          </ModalFooter>
        </Modal>
      </Fragment>
    );
  }

  private renderPanel(form?: IFormikProps) {
    const { multiSelectActive } = this.props.displayPanelProps;
    const { detailsButton } = this.props;
    const classes = classNames(`${DGP}main`, {
      multiselect: multiSelectActive,
      single: !multiSelectActive,
    });
    return (
      <aside className={classes} ref={this.panelContainer}>
        <div className={`${DGP}header`}>
          <Icon icon={['fal', 'times']} size="lg" onClick={this.handleClose} title="close" />
          {this.maybeHamburger()}
        </div>
        <div className={`${DGP}content`}>
          {this.title()}
          {this.maybeIcon()}
          {this.maybe('preview')}
          {this.maybeActions()}
          {form && <div className={`${DGP}form`}>{form.render(form.props)}</div>}
          {this.maybe('kpi')}
          {!!detailsButton && <div className={`${DGP}details-btn`}>{detailsButton}</div>}
        </div>
        {form && this.maybeButtons(form.props)}
      </aside>
    );
  }

  private title() {
    const { title, type } = this.props;
    return type === 'multi' ? this.multiselectTitle() : title && <h2 className={`${DGP}title`}>{title}</h2>;
  }
}

export default DataGridPanel;
